import React from 'react';
import { motion } from 'framer-motion';
import { FaChild, FaUsers, FaHandHoldingHeart, FaChartLine, FaStar, FaQuoteLeft } from 'react-icons/fa';
import './Impact.css';

const Impact = () => {
  const teamMembers = [
    {
      id: 1,
      name: "Sarah Johnson",
      role: "Executive Director",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=300&fit=crop&crop=face",
      bio: "Dedicated to creating lasting change in children's lives through education and support programs."
    },
    {
      id: 2,
      name: "Michael Chen",
      role: "Program Director",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face",
      bio: "Leading our educational initiatives and community outreach programs across multiple regions."
    },
    {
      id: 3,
      name: "Dr. Emily Rodriguez",
      role: "Healthcare Coordinator",
      image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=300&h=300&fit=crop&crop=face",
      bio: "Ensuring access to quality healthcare for all children in our programs."
    },
    {
      id: 4,
      name: "David Thompson",
      role: "Operations Manager",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face",
      bio: "Managing day-to-day operations and ensuring efficient delivery of our services."
    }
  ];

  const impactStats = [
    {
      icon: <FaChild />,
      number: "2,500+",
      label: "Children Supported",
      description: "Direct beneficiaries of our education and healthcare programs"
    },
    {
      icon: <FaUsers />,
      number: "25",
      label: "Communities Reached",
      description: "Villages and urban areas where we operate"
    },
    {
      icon: <FaHandHoldingHeart />,
      number: "₹16.25M+",
      label: "Funds Raised",
      description: "Total donations received to support our programs"
    },
    {
      icon: <FaChartLine />,
      number: "94%",
      label: "Success Rate",
      description: "Children who completed our programs successfully"
    }
  ];

  const testimonials = [
    {
      id: 1,
      name: "Priya Sharma",
      role: "Monthly Donor",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      message: "I've been donating to ATS Foundation for 3 years now. Seeing the monthly reports of how my contributions help children get education and healthcare fills my heart with joy. The transparency and impact are incredible!",
      rating: 5
    },
    {
      id: 2,
      name: "Rajesh Kumar",
      role: "Volunteer",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      message: "As a volunteer, I've seen firsthand how ATS Foundation transforms lives. The dedication of the team and the smiles on children's faces make every moment worthwhile. This is truly making a difference.",
      rating: 5
    },
    {
      id: 3,
      name: "Dr. Meera Patel",
      role: "Healthcare Partner",
      image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face",
      message: "Working with ATS Foundation has been an incredible experience. Their healthcare programs are well-organized and reach the most vulnerable children. The impact on community health is remarkable.",
      rating: 5
    },
    {
      id: 4,
      name: "Amit Singh",
      role: "Corporate Donor",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      message: "Our company has been supporting ATS Foundation for 2 years. The detailed reports and transparency in fund utilization give us confidence that our CSR funds are making real impact.",
      rating: 5
    },
    {
      id: 5,
      name: "Sunita Devi",
      role: "Parent of Beneficiary",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      message: "My daughter received education support from ATS Foundation. Today she's in college pursuing her dreams. This foundation doesn't just help children, they change entire families' futures.",
      rating: 5
    },
    {
      id: 6,
      name: "Krishna Reddy",
      role: "Community Leader",
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
      message: "ATS Foundation has brought positive change to our village. The school they built and the healthcare camps they organize have improved the quality of life for everyone here.",
      rating: 5
    }
  ];

  const successStories = [
    {
      title: "Education Success",
      description: "Over 1,200 children have completed their primary education through our support programs",
      percentage: 95,
      color: "#667eea"
    },
    {
      title: "Healthcare Impact",
      description: "More than 800 children received essential healthcare and vaccinations",
      percentage: 92,
      color: "#48bb78"
    },
    {
      title: "Community Development",
      description: "15 villages now have improved infrastructure and access to basic amenities",
      percentage: 88,
      color: "#ed8936"
    },
    {
      title: "Skill Development",
      description: "500+ youth trained in vocational skills for better employment opportunities",
      percentage: 90,
      color: "#e53e3e"
    }
  ];

  return (
    <section id="impact" className="impact-section">
      <div className="container">
        <motion.div 
          className="impact-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Our Impact</h2>
          <p className="section-subtitle">
            See the real difference your support makes in the lives of underprivileged children and communities.
            Every rupee donated creates lasting positive change.
          </p>
        </motion.div>

        {/* Impact Statistics */}
        <motion.div 
          className="impact-stats"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="stats-grid">
            {impactStats.map((stat, index) => (
              <motion.div 
                key={index}
                className="stat-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="stat-icon">
                  {stat.icon}
                </div>
                <h3>{stat.number}</h3>
                <h4>{stat.label}</h4>
                <p>{stat.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Success Stories */}
        <motion.div 
          className="success-stories"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <h3>Success Stories</h3>
          <div className="stories-grid">
            {successStories.map((story, index) => (
              <motion.div 
                key={index}
                className="story-card"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="story-header">
                  <h4>{story.title}</h4>
                  <div className="percentage" style={{ color: story.color }}>
                    {story.percentage}%
                  </div>
                </div>
                <p>{story.description}</p>
                <div className="progress-bar">
                  <motion.div 
                    className="progress-fill"
                    style={{ backgroundColor: story.color }}
                    initial={{ width: 0 }}
                    whileInView={{ width: `${story.percentage}%` }}
                    transition={{ duration: 1.5, delay: 0.5 + index * 0.1 }}
                    viewport={{ once: true }}
                  ></motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Team Section */}
        <motion.div 
          className="team-section"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <h3>Meet Our Team</h3>
          <p className="team-intro">
            Our dedicated team of professionals works tirelessly to ensure that every child receives the support they need.
          </p>
          
          <div className="team-grid">
            {teamMembers.map((member, index) => (
              <motion.div 
                key={member.id}
                className="team-member"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="member-image">
                  <img src={member.image} alt={member.name} />
                </div>
                <div className="member-info">
                  <h4>{member.name}</h4>
                  <span className="role">{member.role}</span>
                  <p>{member.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Testimonials */}
        <motion.div 
          className="testimonials-section"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
        >
          <h3>Messages from Our Supporters</h3>
          <p className="testimonials-intro">
            Hear from the amazing people who support our mission and the families whose lives we've touched.
          </p>
          
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <motion.div 
                key={testimonial.id}
                className="testimonial-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="quote-icon">
                  <FaQuoteLeft />
                </div>
                <p className="testimonial-message">{testimonial.message}</p>
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} className="star" />
                  ))}
                </div>
                <div className="testimonial-author">
                  <img src={testimonial.image} alt={testimonial.name} />
                  <div className="author-info">
                    <h4>{testimonial.name}</h4>
                    <span>{testimonial.role}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div 
          className="impact-cta"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
        >
          <h3>Join Us in Making a Difference</h3>
          <p>
            Your support can help us reach more children and create even greater impact. 
            Every contribution matters in our mission to transform lives.
          </p>
          <button 
            className="btn btn-primary"
            onClick={() => document.getElementById('donate').scrollIntoView({ behavior: 'smooth' })}
          >
            <FaHandHoldingHeart /> Donate Now
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Impact; 